import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { loginRequestSchema } from '@perplexicon/validation';
import type { LoginRequest } from '@perplexicon/validation';

import { assetUrl } from '../../assets/asset-manifest.js';
import { Button, Card, ErrorState, Input } from '../../components/index.js';
import { useLogin } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';

interface LocationState {
  from?: Location;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const {
    register: field,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({ resolver: zodResolver(loginRequestSchema) });

  const onSubmit = handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: () => {
        const state = location.state as LocationState | null;
        const redirectTo = state?.from ? `${state.from.pathname}${state.from.search}` : '/';
        void navigate(redirectTo, { replace: true });
      },
    });
  });

  return (
    <Card variant="parchment">
      <img
        src={assetUrl('decorFlourish')}
        alt=""
        aria-hidden="true"
        width={64}
        height={64}
        className="mb-2 h-7 w-7"
      />
      <h1 className="font-display text-2xl font-semibold text-textPrimary">Log in</h1>
      <form onSubmit={(e) => void onSubmit(e)} noValidate className="mt-4 flex flex-col gap-4">
        <Input
          label="Username"
          autoComplete="username"
          error={errors.username?.message}
          {...field('username')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...field('password')}
        />
        {login.isError && <ErrorState message={getErrorMessage(login.error)} />}
        <Button type="submit" emphasis="decorated" isLoading={login.isPending} className="w-full">
          Log in
        </Button>
      </form>
      <div className="mt-4 flex flex-col items-center gap-2 text-sm text-textSecondary">
        <Link to="/forgot-password" className="font-semibold text-link hover:text-linkHover">
          Forgot your password?
        </Link>
        <p>
          Need an account?{' '}
          <Link to="/register" className="font-semibold text-link hover:text-linkHover">
            Register
          </Link>
        </p>
      </div>
    </Card>
  );
}
